import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PROVIDER_FIELDS, PROVIDER_CATEGORY } from "@/lib/integrations/categories";
import { listConnections } from "@/lib/integrations.functions";
import { ensurePublicApiConnection } from "@/lib/connections/public-api.functions";
import { Plug, ArrowRight, CheckCircle2, AlertTriangle, PlugZap, Sparkles, Code2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { AddConnectionActions } from "@/components/admin/add-connection-actions";
import { useScope } from "@/hooks/use-scope";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/connections/")({
  component: ConnectionsIndex,
});

type Conn = {
  id: string;
  provider: string;
  label: string;
  alias: string | null;
  env: string;
  status: string;
  health_message: string | null;
  last_health_at: string | null;
};

function ConnectionsIndex() {
  const list = useServerFn(listConnections);
  const ensureFn = useServerFn(ensurePublicApiConnection);
  const qc = useQueryClient();
  const { tenantId } = useScope();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "connections", "list"],
    queryFn: () => list(),
  });
  const all = (data?.connections ?? []) as Conn[];
  // Sprint 25 · M4.1 — unified Connections experience: shipping carriers
  // are Connections too. We no longer hide them behind /admin/carriers.
  const conns = all;
  const hasPublicApi = conns.some((c) => c.provider === "public_api");

  const ensure = useMutation({
    mutationFn: () => ensureFn({ data: { tenantId: tenantId! } }),
    onSuccess: () => {
      toast.success("Public API Connection ready");
      qc.invalidateQueries({ queryKey: ["admin", "connections", "list"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const total = conns.length;
  const healthy = conns.filter((c) => operatorState(c.status) === "healthy").length;
  const attention = conns.filter((c) => operatorState(c.status) === "attention").length;
  const disconnected = conns.filter((c) => operatorState(c.status) === "disconnected").length;

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl">
      {/* Page header — Behavior-first framing */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">Connections</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            A Connection is where you tell CairnOS how to run this customer's
            business — how orders flow, what gets blocked, what gets published,
            and how CairnOS responds. Connectivity is table stakes; the
            Behavior is the product.
          </p>
        </div>
        <AddConnectionActions />
      </div>

      {/* Health tiles — three operator-facing states. Classified by
          operator impact, not technical implementation:
            healthy      → functioning normally
            attention    → operational, but something needs review
            disconnected → cannot reliably exchange data */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <HealthTile label="Connections" value={total} tone="neutral" />
        <HealthTile label="Healthy" value={healthy} tone="ok" icon={CheckCircle2} />
        <HealthTile label="Needs Attention" value={attention} tone="warn" icon={AlertTriangle} />
        <HealthTile label="Disconnected" value={disconnected} tone="bad" icon={PlugZap} />
      </div>

      {!isLoading && !hasPublicApi && tenantId && (
        <Card className="border-dashed">
          <CardContent className="p-4 flex items-start gap-3">
            <Code2 className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">Public API</div>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                Treat your customer's own systems as another Connection. The
                Public API has Behavior, Credentials, Activity, and Exceptions
                just like Shopify or CartRover — and routes through the same
                Exchange Platform.
              </p>
            </div>
            <Button size="sm" onClick={() => ensure.mutate()} disabled={ensure.isPending}>
              {ensure.isPending ? "Setting up…" : "Set up Public API"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-muted-foreground">All Connections</h2>
        </div>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : conns.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              No connections yet.
            </CardContent>
          </Card>
        ) : (
          <GroupedConnectionsList conns={conns} />
        )}
      </div>

      {/* Recommendations slot (reserved; nothing renders yet) */}
      <RecommendationsSlot />
    </div>
  );
}

/**
 * Sprint 25 · M4.1 — render Connections grouped by capability category
 * (Shipping, Order Ingestion, Platform, …). Reads category from
 * PROVIDER_CATEGORY; never branches on `provider === "..."`.
 *
 * Display label prefers `alias` over `label` when set — alias is
 * presentation-only and never participates in routing.
 */
function GroupedConnectionsList({ conns }: { conns: Conn[] }) {
  const CATEGORY_ORDER = ["carrier", "order_source", "platform", "other"] as const;
  const CATEGORY_LABEL: Record<string, string> = {
    carrier: "Shipping & Labels",
    order_source: "Order Ingestion",
    platform: "Platform",
    other: "Other",
  };
  const buckets = new Map<string, Conn[]>();
  for (const c of conns) {
    const cat = (PROVIDER_CATEGORY[c.provider] as string | undefined) ?? "other";
    if (!buckets.has(cat)) buckets.set(cat, []);
    buckets.get(cat)!.push(c);
  }
  return (
    <div className="space-y-4">
      {CATEGORY_ORDER.map((cat) => {
        const items = buckets.get(cat) ?? [];
        if (items.length === 0) return null;
        return (
          <div key={cat} className="space-y-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {CATEGORY_LABEL[cat]}
            </div>
            <div className="rounded-lg border divide-y bg-card">
              {items.map((c) => (
                <Link
                  key={c.id}
                  to="/app/admin/connections/$connectionId"
                  params={{ connectionId: c.id }}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors group"
                >
                  <Plug className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium truncate">{c.alias ?? c.label}</span>
                      {c.alias && (
                        <span className="text-[11px] text-muted-foreground truncate">
                          {c.label}
                        </span>
                      )}
                      <span className="text-[11px] uppercase tracking-wide text-muted-foreground shrink-0">
                        {PROVIDER_FIELDS[c.provider]?.label ?? c.provider}
                      </span>
                      <span className="text-[11px] text-muted-foreground shrink-0">· {c.env}</span>
                    </div>
                    {c.health_message && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {c.health_message}
                      </p>
                    )}
                  </div>
                  <StatusBadge status={c.status} />
                  <ArrowRight className="h-4 w-4 text-muted-foreground/60 group-hover:text-foreground shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HealthTile({
  label, value, tone, icon: Icon,
}: {
  label: string;
  value: number;
  tone: "neutral" | "ok" | "warn" | "bad";
  icon?: typeof CheckCircle2;
}) {
  const toneCls =
    tone === "ok" ? "text-emerald-600 dark:text-emerald-400"
    : tone === "warn" ? "text-amber-600 dark:text-amber-400"
    : tone === "bad" ? "text-red-600 dark:text-red-400"
    : "text-foreground";
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
          {Icon && <Icon className={cn("h-3.5 w-3.5", toneCls)} />}
          {label}
        </div>
        <div className={cn("mt-1 text-2xl font-semibold tabular-nums", toneCls)}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Collapse the raw connection status into one of three operator-facing
 * states. Anything that still allows the connection to operate but
 * requires intervention rolls up under "attention". Only states that
 * mean the connection cannot reliably exchange data become
 * "disconnected".
 */
function operatorState(status: string): "healthy" | "attention" | "disconnected" {
  if (status === "active") return "healthy";
  if (status === "error") return "disconnected";
  return "attention";
}

function StatusBadge({ status }: { status: string }) {
  const state = operatorState(status);
  const variant =
    state === "healthy" ? "default"
    : state === "disconnected" ? "destructive"
    : "secondary";
  const label =
    state === "healthy" ? "Healthy"
    : state === "disconnected" ? "Disconnected"
    : "Needs Attention";
  return (
    <Badge variant={variant as "default" | "destructive" | "secondary"} className="shrink-0">
      {label}
    </Badge>
  );
}

/**
 * Reserved location for intelligent recommendations.
 * Renders nothing today. The recommendation engine is intentionally not
 * built in Pass 1 — but the slot exists so future passes can drop in
 * guidance like "Most unknown SKUs are manually approved — consider
 * enabling auto-create" without redesigning this page.
 */
function RecommendationsSlot() {
  const recommendations: { id: string; title: string }[] = [];
  if (recommendations.length === 0) return null;
  return (
    <div className="rounded-lg border bg-muted/30 p-4">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5" /> Recommendations
      </div>
    </div>
  );
}
